/**
 * The estimate panel and Start run (sheet 11 callouts 8–13, sheet 51 callouts
 * 1–3; FR-009; B.3, B.5, GEN.5, GEN.7, D.8, TX.8, X.10; research §1.16;
 * contracts/ui-api.md "New run"; post-plan C12; post-tasks P1, P9).
 *
 * The estimate is read with `estimateQuery` (staleTime 0, refetched on focus:
 * credits bought in another tab change the answer). Three mono stat cards, the
 * free items, the reserved sentence, then the band slot and the panel's one
 * primary, "Start run".
 *
 * - Blocked (B.3): the blocked band holds the estimate's `message` verbatim
 *   with the Buy credits remedy, and Start is disabled with that reason.
 * - Soft cap (B.5): the caption states it; the styled warning band and its
 *   email are mission 9's (C-013).
 * - Start: 201 or 200 invalidates `keys.runsRoot` only, then navigates to the
 *   run. The credits are not invalidated here: the navigation owns the credit
 *   read (P9, NFR-005). A 402 holds Start until the refetched estimate lands,
 *   and its band leaves with the hold (mission 8 WP10 T053; FR-020 A-L1): the
 *   new estimate speaks for itself, blocked or not; a
 *   400 hands the field errors to the per-run parameters; everything else is a
 *   ProblemBand, whose `run_id` rule for `upload-not-startable` is the gate
 *   table's (C12).
 * - Path B states the run can never be transmitted (X.10, register 2).
 *
 * Mission 9 WP14 T087 (sheet 11/12, 11/13, 51/4, 51/5, 51/9; FR-015, FR-019, FR-022; B.1, B.3, B.5;
 * OR-12, OR-13; squad U11, U20, U25, U30):
 * - The soft cap is the styled warning band (`softCapWarning`, `softCapAccounting`). Its figures come from
 *   the estimate and the credits read only, never `GET /v1/billing` (U20): the overage is
 *   `required − balance_before`, the ceiling `forgiveness_used + forgiveness_remaining`, the date
 *   `credits.cycle_end` (on an annual plan `billing.period_end` is the year's end). A credits body
 *   without the cycle members (mission 3) keeps the caption.
 * - The blocked band offers `blockedBuyCredits` and states `blockedEmailNote`: the estimate answering
 *   blocked is the email's trigger (OR-12), so the note appears there and nowhere else.
 * - `MeteringStatement` sits beside the free items (OR-13; the copy owner settles the placement).
 * - Start is refused through `useBillingGate` (U11): demoted to secondary and relabeled `gatedReadOnly`,
 *   the reason in its tooltip (U30).
 * - One page band (§7.5; U12): a start refusal's ProblemBand replaces the blocked and soft-cap bands, so
 *   with the shell's billing banner the page stacks two at most. Billing problems render their register
 *   rows (the spine's ProblemBand), and the held Start's reason names `detail` for mission 3's
 *   `insufficient-credits` only (U25).
 * - UI8 (FR-022; known-followups follow-up 9): the hold reads the estimate's `dataUpdatedAt` from the
 *   query cache when the 402 lands, not the render's value, so an estimate that refetched during the
 *   start cannot end the hold, and clear the band, at once.
 */
import { useId, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'

import { Band } from '@/components/trellis/Band'
import { ProblemBand } from '@/components/trellis/ProblemBand'
import { StatCard } from '@/components/trellis/StatCard'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { ApiError, idSegment, type Problem } from '@/lib/api'
import type { Credits } from '@/lib/api/credits'
import { startRun, type RunPath } from '@/lib/api/runs'
import type { Estimate } from '@/lib/api/uploads'
import { gateButton, useBillingGate } from '@/lib/billing-gate'
import { count, date } from '@/lib/format'
import { PROBLEM, resolveProblem } from '@/lib/problems'
import { creditsQuery, estimateQuery, keys } from '@/lib/queries'
import { PROVISIONAL_COPY, copyText, freeItemsList } from '@/lib/provisional-copy'
import { MeteringStatement } from '@/routes/subscribe/MeteringStatement'

import { PER_RUN_FIELDS, type PerRunErrors, type PerRunField, type PerRunValue } from './PerRunParameters'

export interface EstimatePanelProps {
  uploadId: string
  path: RunPath
  /** The filled per-run fields only (Path A); Path B passes none. */
  perRun: Partial<PerRunValue>
  /** The layout document's admission cap, for the GEN.7 expectation line. */
  admissionCap?: number | undefined
  /** A 400 `validation-failed` at start, by field. */
  onFieldErrors: (errors: PerRunErrors) => void
}

/** A problem the SPA did not receive from the API: the register 16 fallback. */
const GENERIC: Problem = { type: 'about:blank', title: '', status: 0 }

/** The free items as style guide §8's caption list of four (owner decision 2026-09-26, decision 17). */
function FreeItems() {
  const { lead, items } = freeItemsList()
  const leadId = useId()
  return (
    <div className="text-caption text-navy-600">
      <p id={leadId}>{lead}</p>
      <ul aria-labelledby={leadId} className="list-disc pl-5">
        {items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    </div>
  )
}

/** The soft-cap band's figures (U20): the estimate's overage, and the ceiling and close date of the credits read. */
function softCapFigures(estimate: Estimate, credits: Credits | undefined) {
  if (credits?.cycle_end == null || credits.forgiveness_used === undefined || credits.forgiveness_remaining === undefined) return null
  const forgiven = credits.forgiveness_used
  return {
    overage: estimate.required - estimate.balance_before,
    ceiling: forgiven + credits.forgiveness_remaining,
    forgiven,
    date: date(credits.cycle_end),
  }
}

function isPerRunField(field: string): field is PerRunField {
  return (PER_RUN_FIELDS as readonly string[]).includes(field)
}

export function EstimatePanel({ uploadId, path, perRun, admissionCap, onFieldErrors }: EstimatePanelProps) {
  const estimate = useQuery(estimateQuery(uploadId))
  const credits = useQuery(creditsQuery())
  const gate = useBillingGate()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [starting, setStarting] = useState(false)
  const startingRef = useRef(false)
  const [startProblem, setStartProblem] = useState<Problem | null>(null)
  /** After a 402, Start waits for an estimate newer than this. */
  const [heldAt, setHeldAt] = useState<number | null>(null)

  const held = heldAt !== null && estimate.dataUpdatedAt === heldAt
  // A-L1: the refetched estimate ends the hold, and the 402's band with it.
  if (heldAt !== null && !held) {
    setHeldAt(null)
    setStartProblem(null)
  }

  async function start() {
    if (startingRef.current) return
    startingRef.current = true
    setStarting(true)
    setStartProblem(null)
    onFieldErrors({})
    try {
      const run = await startRun({ upload_id: uploadId, ...perRun })
      const to = `/app/runs/${idSegment(run.run_id, 'run id')}`
      void queryClient.invalidateQueries({ queryKey: keys.runsRoot })
      navigate(to)
    } catch (err) {
      if (!(err instanceof ApiError)) {
        setStartProblem(GENERIC)
        return
      }
      const p = err.problem
      if (p.type === PROBLEM.VALIDATION_FAILED) {
        const byField: PerRunErrors = {}
        for (const e of p.errors ?? []) if (isPerRunField(e.field)) byField[e.field] = e.message
        if (Object.keys(byField).length > 0) onFieldErrors(byField)
        else setStartProblem(GENERIC)
        return
      }
      if (p.type === PROBLEM.INSUFFICIENT_CREDITS) {
        // UI8: the estimate as it stands now, which may have refetched during the start.
        setHeldAt(queryClient.getQueryState(keys.estimate(uploadId))?.dataUpdatedAt ?? estimate.dataUpdatedAt)
        void queryClient.invalidateQueries({ queryKey: keys.estimate(uploadId) })
      }
      setStartProblem(p)
    } finally {
      startingRef.current = false
      setStarting(false)
    }
  }

  if (estimate.isError) {
    const p = estimate.error instanceof ApiError ? estimate.error.problem : GENERIC
    return (
      <Card>
        <ProblemBand problem={p} />
      </Card>
    )
  }

  const data = estimate.data
  const loading = estimate.isPending
  const softCap = data ? softCapFigures(data, credits.data) : null

  let startButton
  if (gate.refused) {
    startButton = (
      <Button className="w-full" variant="secondary" {...gateButton(gate)}>
        {copyText('gatedReadOnly')}
      </Button>
    )
  } else if (loading) {
    startButton = (
      <Button className="w-full" disabled disabledReason={copyText('loading')}>
        Start run
      </Button>
    )
  } else if (data?.blocked) {
    startButton = (
      <Button className="w-full" disabled disabledReason={data.message}>
        Start run
      </Button>
    )
  } else if (held && startProblem) {
    // U25: `detail` for mission 3's insufficient-credits only; any other problem names its register remedy.
    const detail = startProblem.type === PROBLEM.INSUFFICIENT_CREDITS ? startProblem.detail : undefined
    const reason = detail ?? resolveProblem(startProblem).remedy ?? copyText('genericError')
    startButton = (
      <Button className="w-full" disabled disabledReason={reason}>
        Start run
      </Button>
    )
  } else {
    startButton = (
      <Button className="w-full" loading={starting} onClick={() => void start()}>
        Start run
      </Button>
    )
  }

  return (
    <Card className="flex flex-col gap-4">
      <div className="grid grid-cols-3 gap-4">
        <StatCard label="Credits required" value={data ? count(data.required) : null} mono loading={loading} />
        <StatCard label="Balance before" value={data ? count(data.balance_before) : null} mono loading={loading} />
        <StatCard label="Balance after" value={data ? count(data.balance_after) : null} mono loading={loading} />
      </div>
      <FreeItems />
      <MeteringStatement className="text-caption text-navy-600" />
      <p className="text-caption text-navy-600">{copyText('reservedUntilReady')}</p>
      {path === 'generate' && admissionCap !== undefined ? (
        <p className="text-caption text-navy-600">{PROVISIONAL_COPY.typicalTimeAtCap.render({ admissionCap })}</p>
      ) : null}
      {path === 'analyze' ? (
        <Band kind="info" requirement="X.10">
          {copyText('pathBNotTransmissible')}
        </Band>
      ) : null}
      {startProblem ? (
        <ProblemBand problem={startProblem} />
      ) : data?.blocked ? (
        <Band
          kind="blocked"
          requirement="B.3"
          action={
            <Link to="/app/settings/billing" className="text-body-sm text-[var(--color-link)] underline hover:text-[var(--color-link-hover)]">
              {copyText('blockedBuyCredits')}
            </Link>
          }
        >
          <p>{data.message}</p>
          <p className="mt-1 text-body-sm text-navy-700">{copyText('blockedEmailNote')}</p>
        </Band>
      ) : data?.soft_cap_active && softCap ? (
        <Band kind="warning" requirement="B.5">
          <p>{PROVISIONAL_COPY.softCapWarning.render(softCap)}</p>
          <p className="mt-1 text-body-sm text-navy-700">{PROVISIONAL_COPY.softCapAccounting.render(softCap)}</p>
        </Band>
      ) : data?.soft_cap_active ? (
        <p data-soft-cap="" className="text-caption text-navy-600">
          {copyText('softCapCaption')} {data.message}
        </p>
      ) : null}
      {startButton}
    </Card>
  )
}
